'use client';

import { type QualityAssuranceProcedure } from '@/domain/qualityAssurance/types';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined';
import { useMemo, useState } from 'react';

import ProcedureDetailDialog from './ProcedureDetailDialog';

type ProcedureDataTableProps = {
  procedures: QualityAssuranceProcedure[];
};

type OpenDetail = (procedure: QualityAssuranceProcedure) => void;

const detailButtonTemplate = (procedure: QualityAssuranceProcedure, onOpenDetail: OpenDetail) => (
  <Tooltip title="Details">
    <IconButton
      variant="outlined"
      color="primary"
      size="small"
      onClick={() => {
        onOpenDetail(procedure);
      }}
    >
      <OpenInFullOutlined />
    </IconButton>
  </Tooltip>
);

const buildColumns = (onOpenDetail: OpenDetail): GridColDef<QualityAssuranceProcedure>[] => [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
  },
  {
    field: 'detail',
    headerName: '',
    sortable: false,
    width: 50,
    renderCell: (params) => detailButtonTemplate(params.row, onOpenDetail),
  },
];

const getRowId = (row: QualityAssuranceProcedure) => row.idQualityAssuranceProcedure;

const ProcedureDataTable = ({ procedures }: ProcedureDataTableProps) => {
  const [selectedProcedure, setSelectedProcedure] = useState<QualityAssuranceProcedure | undefined>(undefined);

  const columns = useMemo(() => buildColumns(setSelectedProcedure), []);

  const closeDetail = () => {
    setSelectedProcedure(undefined);
  };

  return (
    <Box sx={(theme) => ({ [theme.breakpoints.down('lg')]: { margin: 2 } })}>
      <Box>
        <DataGrid
          columns={columns}
          rows={procedures}
          density="compact"
          getRowId={getRowId}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />

        {selectedProcedure && <ProcedureDetailDialog selectedProcedure={selectedProcedure} onClose={closeDetail} />}
      </Box>
    </Box>
  );
};

export default ProcedureDataTable;
